import {
  JSXElementConstructor,
  MutableRefObject,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Box, SxProps, Theme } from "@mui/material";
import { grey } from "@mui/material/colors";
import {
  DataGridPro,
  GridApi,
  GridCallbackDetails,
  GridColDef,
  GridDensity,
  GridEventListener,
  GridFilterItem,
  GridFilterModel,
  GridLogicOperator,
  GridPaginationModel,
  GridRowSelectionModel,
  GridRowsProp,
  GridSortModel,
  GridValidRowModel,
  UncapitalizedGridProSlotsComponent,
  gridDensitySelector,
  gridFilterModelSelector,
  useGridApiRef,
} from "@mui/x-data-grid-pro";


import LoadingSpinner from "../Loading";
import { AdvancedSearchToolbar } from "./AdvancedSearchToolbar";
import "./datagrid.css";

import type {
  ActionMenuItem,
  ActionMenuEntry,
  ActionMenuSubheader,
} from "./ActionMenu";
import type { AdvancedSearchFormProps } from "./AdvancedSearch";
import { AdvancedSearch2, AdvancedSearchForm2 } from "./AdvanceSearchUpdate";
import FilterApi, {
  FilterDelete,
  FilterGetApi,
  UserDisplayPreference,
  UserDisplayPreferenceGet,
  UserSortPreference,
  UserSortPreferenceGet,
} from "./filterApi";
import { USER_FILTER_EDIT_REASONS } from "./filterEditReasons";
import { useDataGridStore } from "../../store/DataGrid";
import { DATA_GRID_HEADER_KEYLINE, LISTING_GUTTER, listCellLinkCssSx } from "../../utils/dataGridSx";
import { SortPreference } from "./filter";
import { savedColumnVisibility } from "./savedColumnVisibility";
import { RenamedFields, renameSavedColumns, renameSortModel } from "./renamedFields";
// Javascript compatible object as const alternative to enum
// https://www.typescriptlang.org/docs/handbook/enums.html#objects-vs-enums
export let Actions = {
  AdvancedSearch: "ADVANCED_SEARCH",
  Email: "EMAIL",
  Settings: "SETTINGS",
  None: "",
} as const;
export type ActionType = (typeof Actions)[keyof typeof Actions];

export interface QueryResults<T> {
  total: number;
  rows: T[];
}

export interface AdvancedSearchProps {
  onSearch?: (
    model: GridFilterModel,
    apiRef: MutableRefObject<GridApi>
  ) => void;
  items?: GridFilterItem[];
  [x: string | number | symbol]: unknown;
}

export interface AeronetDataGridProps<T extends GridValidRowModel> {
  actions?: ActionMenuEntry[];
  advancedSearchForm?: JSXElementConstructor<AdvancedSearchFormProps>;
  advancedSearchProps?: AdvancedSearchProps;
  checkboxSelection?: boolean;
  children?: (props: {
    onClose: () => void;
    action: ActionType;
    Actions: { [key: string]: ActionType };
  }) => React.ReactNode;
  columns: GridColDef<T>[];
  components?: Partial<UncapitalizedGridProSlotsComponent>;
  currentUserId: number;
  data: (
    params: { [key: string]: any },
    config?: { [key: string]: any },
    apiRef?: MutableRefObject<GridApi>
  ) => Promise<QueryResults<T>>;
  disableColumnFilter?: boolean;
  enableAdvancedSearch?: boolean;
  enableCreate?: boolean;
  createMenu?: React.ReactNode;
  enableDelete?: boolean;
  enableFilter?: boolean;
  exportFileName?: string;
  initialSort?: GridSortModel;
  /**
   * Filter items the grid STARTS with — a Dashboard tile's link into the list
   * (PATTERNS.md BP-93; build them with `useUrlFilterItems` in `./urlFilter`).
   * They seed the filter the first fetch sends AND the Filters panel, so the
   * user sees what narrowed the list and can clear it there.
   *
   * Read once, on mount: pass `key={urlFilterKey(items)}` so a different seed
   * remounts the grid. While seeded, the user's SAVED filter is not restored —
   * the link says what to show, and a saved filter layered on top would
   * silently narrow it to something the tile never counted. Omitted or empty,
   * nothing changes: every grid that does not pass it behaves as before.
   */
  initialFilterItems?: GridFilterItem[];
  /**
   * A list's STANDING default filter — what it shows a user who has saved no
   * filter of their own (Aircraft: Status is Active, A422). It lands in
   * the Filters panel, so the user sees what narrowed the list and can change
   * or clear it there.
   *
   * Unlike `initialFilterItems` (a link's one-off seed, which suppresses the
   * saved filter), a SAVED filter always wins over this: it is applied only
   * once the saved-filter read comes back empty. It is applied as a restore,
   * so loading the page writes nothing to `/v6/filters`; the user's own edit
   * is saved by Display Preferences → Save, like any filter edit. The first fetch goes out before that read returns, so the
   * `data` source must apply the same default when the request carries no
   * filter for that field. Read once, on mount.
   */
  defaultFilterItems?: GridFilterItem[];
  /**
   * Rows per page on FIRST load, for a small bounded admin list the operator
   * wants to scan in one view instead of paging. Must be one of
   * `pageSizeOptions` = [15, 50, 100]; anything else renders a page size MUI's
   * size selector cannot represent. Defaults to 15 — every list that does not
   * pass it is unchanged.
   *
   * A SAVED page size still wins. Both the restore path (grid state) and the
   * `pagination[name]` store below overwrite this once the user has chosen a
   * size for this grid, which is the intended precedence: this is the initial
   * default, not an override.
   *
   * ⚠️ THIS PROP WAS DEAD FROM 2026-08-21 TO 2026-09-04 and is being restored,
   * not added. PATTERNS.md documented it as production-proven and cited "a
   * component change in src/components/datagrid/DataGrid.tsx" — but the change
   * was not here: the interface had no such entry, so `initialPageSize` fell
   * through the `[x]: unknown` index signature straight to DataGridPro, which
   * ignores it. `src/modules/ModulesList.tsx` has been passing
   * `initialPageSize={100}` into that void, silently capped at 15 rows, and
   * `src/cycle-count/CycleCountList.tsx` carries a comment from a sibling who
   * hit the same wall and worked around it. Lost in the 2026-08-21 GitLab sync
   * (clobber item H3, rank 60 of 76 — small enough to be invisible to any
   * net-lines threshold). Restoring the prop keeps the 15 default, so the
   * measured blast radius is the one file that passes it, not the 121 files
   * that mount this grid.
   */
  initialPageSize?: number;
  name: string;
  onCellClick?: GridEventListener<"cellClick">;
  onCreate?: () => void;
  onDelete?: (GridRowSelectionModel) => void;
  onRowClick?: GridEventListener<"rowClick">;
  setSelected?: (selected?: T) => void;
  sx?: SxProps<Theme>;
  quickSearchItems?: GridFilterItem[];
  /** When false, do not sync quick filter into search field state (toolbar search box). Filter still applied via filterObj. Default true. */
  quickSearchValueDisplay?: boolean;
  /** Optional trailing slot rendered at the end of the toolbar row, just before the kebab. Forwarded to AdvancedSearchToolbar via slotProps. */
  toolbarExtras?: React.ReactNode;
  /** Optional slot rendered inside the kebab (⋮) menu's "Settings" section,
   *  immediately after the built-in "Display" item — for module-level settings
   *  entry points. Opt-in; omitted leaves the menu unchanged. The node is cloned
   *  with `hideMenu: () => void` so it can close the popup on click. Forwarded to
   *  AdvancedSearchToolbar via slotProps. */
  settingsMenuExtras?: React.ReactNode;
  /**
   * Replaces the bare "No rows" empty state for lists that scope themselves
   * OUTSIDE the grid's own filter model — a day-scoped list, a tenant-scoped
   * list, anything appending a predicate inside its `data` callback.
   *
   * Why this needs a prop at all: the overlay below picks its wording from
   * `filterObj`/`filterActive`/`advanceSearchTriggered`, which only know about
   * filters the GRID applied. A scope the caller merged into its own fetch is
   * invisible to all three, so the grid truthfully reports "No rows" while the
   * user is in fact looking at one narrow slice — and "No rows" then reads as
   * "this module is empty", or worse "this module is broken". Load Sheets is
   * the case that prompted it: it defaults to TODAY and g2 has 48 flights
   * spanning Jul-Aug 2026 with 0 on today's date, so a correct, resolved,
   * non-empty module renders a blank grid that looks stuck.
   *
   * Only shown when no grid-level filter or advanced search is active — a
   * filtered no-match still gets the filter wording, which is the more
   * specific and more useful message.
   */
  noRowsMessage?: React.ReactNode;
  onRowSelectionModelChange?: (model: GridRowSelectionModel) => void;
  [x: string | number | symbol]: unknown;
  isHeaderBorder?: boolean;
  /**
   * OLD field -> NEW field, for a column whose `field` was renamed. The user's
   * SAVED layout (order, width, visibility) and saved sort are read under the
   * new name, so the column keeps its place instead of jumping to the end.
   * Saved filters are not renamed — see renamedFields.ts.
   */
  renamedFields?: RenamedFields;
}


function AeronetDataGrid<T extends GridValidRowModel>(
  props: AeronetDataGridProps<T>
) {
  const {
    actions,
    advancedSearchForm = AdvancedSearchForm2,
    checkboxSelection = true,
    children,
    components = { toolbar: AdvancedSearchToolbar  },
    currentUserId,
    data,
    disableColumnFilter = true,
    enableAdvancedSearch = true,
    enableCreate = true,
    enableDelete = true,
    enableFilter = false,
    exportFileName = "Aeronet",
    initialSort,
    initialFilterItems,
    defaultFilterItems,
    initialPageSize = 15,
    name,
    onDelete,
    quickSearchItems,
    quickSearchValueDisplay = true,
    toolbarExtras,
    settingsMenuExtras,
    noRowsMessage,
    setSelected,
    sx = [],
    isHeaderBorder = false,
    renamedFields,
    ...restProps
  } = props;

  // ⚠️ PULL `slots` OUT OF THE PASSTHROUGH AND MERGE IT (2026-07-29).
  // This component sets its own slots={{toolbar, noRowsOverlay}} below and then
  // spreads the passthrough LAST, so a consumer passing ANY `slots` used to
  // REPLACE the whole object and silently lose the toolbar (search / create /
  // delete / filter / export) and the empty-state overlay. No error, no warning
  // -- the affordances just vanished.
  //
  // That made a legitimate MUI pattern unexpressible: master-detail wants
  // slots={{detailPanelExpandIcon, detailPanelCollapseIcon}} to get the standard
  // chevrons (as src/quotes/RFQ-from-email/RFQRootDataGrid.tsx does for Quotes
  // Build), and asking for chevrons should not cost you the toolbar. Merging
  // instead means a consumer ADDS slots by default and still overrides one
  // deliberately when it means to (Build passes toolbar: () => null on purpose).
  const {
    slots: consumerSlots,
    slotProps: consumerSlotProps,
    ...dataGridProps
  } = restProps as {
    slots?: Record<string, unknown>;
    slotProps?: Record<string, any>;
  } & Record<string, unknown>;

  const { items: defaultItems, ...advancedSearchProps } =
    props.advancedSearchProps ?? {};

  const [action, setAction] = useState<ActionType>(Actions.None);
  // BP-93: a seeded grid starts filtered — both the model the fetch reads and
  // the one the Filters panel shows. Unseeded, both start undefined as before.
  const [seededFilter] = useState<GridFilterModel | undefined>(() =>
    initialFilterItems && initialFilterItems.length > 0
      ? { items: initialFilterItems.map((item) => ({ ...item })) }
      : undefined
  );
  // Read once: a consumer passing a fresh array each render must not re-run
  // the saved-filter read below.
  const [standingDefaultFilter] = useState<GridFilterItem[]>(() =>
    (defaultFilterItems ?? []).map((item) => ({ ...item }))
  );
  const [filter, setFilter] = useState<GridFilterModel | undefined>(seededFilter);
  const [filterVisibleModel, setFilterVisibleModel] = useState<GridFilterModel | undefined>(seededFilter);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  // Seeded from the `initialPageSize` prop (default 15). Seeding ONLY here is
  // not enough — the page-size effect further down resets to a literal on every
  // `pagination`/`name` change and would stomp this on the first run for any
  // grid with no saved pagination. Both sites take the prop; see there.
  const [pageSize, setPageSize] = useState(initialPageSize);
  // -1 is MUI's "unknown total" sentinel. In paginationMode="server" it makes
  // TablePagination render `of more than <to>` (see @mui/material
  // TablePagination.js: ``count !== -1 ? count : `more than ${to}` ``), which is
  // what produced the `-14–0 of more than 0` footer on Components > Flying while
  // its request was still in flight. 0 is the honest pre-first-response total and
  // is pagination-equivalent: getPageCount() returns 0 for both -1 and 0.
  const [rowCount, setRowCount] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(
    []
  );
  // H137d. Seeded from `initialSort` so the consumer's default order is what
  // the FIRST fetch asks for. It used to start undefined, and `initialSort`
  // was only ever applied from the clear branch of onSortChange — so on a
  // fresh load no `sort` was sent and every list showed the API's default
  // order, whatever its initialSort said. A saved per-user sort preference
  // (the effect below) still replaces this once it loads.
  const [sortModel, setSortModel] = useState<GridSortModel | undefined>(
    initialSort
  );
  const sortModelRef = useRef(sortModel);
  sortModelRef.current = sortModel;
  // A Filters-panel edit waiting for Display Preferences → Save: the items to
  // save ([] = delete the saved filter), or undefined when nothing changed.
  const pendingFilterRef = useRef<GridFilterItem[] | undefined>(undefined);
  const [rows, setRows] = useState<GridRowsProp>([]);
  const [filterButtonEl, setFilterButtonEl] =
    useState<HTMLButtonElement | null>(null);
  const [buttonText, setButtonText] = useState("");
  const [debouncedValue, setDebouncedValue] = useState("");
  const [searchField, setSearchField] = useState("");
  const [filterObj, setFilterObj] = useState<GridFilterItem>();
  const [filterItems, setFilterItems] = useState<GridFilterItem[]>([]);
  const [advanceSearchClicked, setAdvanceSearchClicked] = useState(false);
  const {
    advanceSearch,
    setAdvanceSearch,
    advanceSearchTriggered,
    setAdvanceSearchTriggered,
    pagination,
    setPagination,
    quickFilter,
    setQuickFilter,
    setQuickSearchField,
    setOperator,
    resetQuickFilter,
    setQuickSearchValue,
    setFilterActive,
    filterActive
  } = useDataGridStore();
  // load the query params

  const apiRef = useGridApiRef();

  window.addEventListener("beforeunload", () => {
    const localStorageKey = getLocalStorageKey(currentUserId);
    localStorage.removeItem(localStorageKey);
  });

  const getLocalStorageKey = useCallback(
    (userId) => {
      return `aeronet-${name}-state-${userId}`;
    },
    [name]
  );
  useEffect(() => {
    let advanceSearchStored: boolean = false;
    if (advanceSearch.hasOwnProperty(name) && advanceSearch[name].length > 0) {
      setAdvanceSearchClicked(true);
      setAdvanceSearchTriggered(true);
      apiRef.current.setFilterModel(
        { items: advanceSearch[name] },
        "upsertFilterItems"
      );
      advanceSearchStored = true;
    } 
    
    else if (seededFilter) {
      // BP-93: seeded from a link — say a filter is active (the empty-state
      // wording reads it) and do NOT restore the saved filter over the seed.
      setFilterActive(true);
    }
    else {
      if (name && (!advanceSearchClicked || !advanceSearchStored)) {
        FilterGetApi(name).then((response) => {
          if (response && response.json_string) {
            let filterItem = JSON.parse(response?.json_string);
            setFilterItems(filterItem);
            setFilterActive(true);
          } else if (standingDefaultFilter.length > 0) {
            // No saved filter: the list's standing default. "restoreState"
            // sets the model without staging it as a user edit to save.
            apiRef.current.setFilterModel(
              { items: standingDefaultFilter.map((item) => ({ ...item })) },
              "restoreState"
            );
            setFilterActive(true);
          }
        });
      }
    }
  }, [
    name,
    apiRef,
    advanceSearchClicked,
    advanceSearch,
    setAdvanceSearchTriggered,
    setAdvanceSearchClicked,
    setFilterActive,
    seededFilter,
    standingDefaultFilter,
  ]);

  useEffect(() => {
    if (filterItems.length > 0) {
      apiRef.current.upsertFilterItems(filterItems);
    }
  }, [filterItems, apiRef]);

  useEffect(() => {
    if (quickFilter.hasOwnProperty(name)) {
      setFilterObj(quickFilter[name]);
      if (quickSearchValueDisplay) {
        setSearchText(quickFilter[name].value);
        setQuickSearchField(quickFilter[name].field);
        setOperator(quickFilter[name].operator);
        setSearchField(quickFilter[name].field);
      }
    }
  }, [
    quickFilter,
    name,
    quickSearchValueDisplay,
    setFilterObj,
    setSearchText,
    setQuickSearchField,
    setOperator,
    apiRef,
  ]);
  /**
   * Combines the actions prop with default Actions.
   * Flattens ActionMenuSubheader children so their action types are registered.
   */
  useMemo(() => {
    console.debug("Actions memo");
    if (actions && actions.length > 0) {
      let additionalActions = {};
      for (const entry of actions) {
        if ("subheader" in entry && "children" in entry) {
          for (const child of (entry as ActionMenuSubheader).children) {
            if (child.action) {
              additionalActions = {
                ...additionalActions,
                ...Object.fromEntries([child.action]),
              };
            }
          }
        } else {
          const itemAction = (entry as ActionMenuItem).action;
          if (itemAction) {
            additionalActions = {
              ...additionalActions,
              ...Object.fromEntries([itemAction]),
            };
          }
        }
      }
      Actions = { ...Actions, ...additionalActions };
    }
  }, [actions]);

  const exportState = useCallback(() => {
    if (name) {
      let state = apiRef.current.exportState();
      // Hack to add density into state
      const density = gridDensitySelector(
        apiRef.current.state,
        // @ts-ignore
        apiRef.current.instanceId
      );

      // @ts-ignore
      state.density = density;

      UserDisplayPreference(state, name);

      // The last clicked column sort should only save when clicking save in the preferences
      const activeSortModel = sortModelRef.current ?? state.sorting?.sortModel ?? [];
      UserSortPreference(activeSortModel, name);

      // Likewise the Filters-panel edits since the last Save (onFilterChange stages them).
      const pendingFilter = pendingFilterRef.current;
      if (pendingFilter !== undefined) {
        pendingFilterRef.current = undefined;
        if (pendingFilter.length > 0) {
          FilterApi(pendingFilter, name);
        } else {
          FilterDelete(name);
        }
      }
    }
  }, [apiRef, name]);

  // Read through a ref: the restore effects below run once per grid `name`,
  // and a consumer's inline `renamedFields={{...}}` must not re-run them.
  const renamedFieldsRef = useRef(renamedFields);
  renamedFieldsRef.current = renamedFields;
  // Same for an inline `initialSort={[...]}`: re-running the sort restore on a
  // page re-render would re-read /v6/sort and undo the user's clicked sort.
  const initialSortRef = useRef(initialSort);
  initialSortRef.current = initialSort;

  // sorting model get from db
   /**
   * Replaces the initial sort model (seeded from the initialSort prop) with
   * the user's saved sort preference, when one exists.
   */
   useEffect(() => {
    if (name) {
        UserSortPreferenceGet(name).then((response:SortPreference) => {
          if (response && response.json_string) {
            let savedSort = JSON.parse(response?.json_string);
            if (Array.isArray(savedSort) && savedSort.length > 0) {
              setSortModel(renameSortModel(savedSort, renamedFieldsRef.current));
            } else if (initialSortRef.current) {
              setSortModel(initialSortRef.current);
            }
          }
        });
      }
  }, [
    name
  ]);

  const initialColumnVisibilityDefaultsRef = useRef<Record<string, boolean>>(
    (dataGridProps as {
      initialState?: {
        columns?: {
          columnVisibilityModel?: Record<string, boolean>;
        };
      };
    })?.initialState?.columns?.columnVisibilityModel || {}
  );


  /**
   * Restores state from local storage
   * - ignores filters as they override the initial filters
   */
  
  useEffect(() => {
    if (name && apiRef) {
      UserDisplayPreferenceGet(name).then((response) => {
        if (response && response.json_string) {
          const displayPreference = JSON.parse(response?.json_string);
          // Never restore UI panel open/close state (e.g. filter panel).
          // We intentionally persist user display prefs like columns/pagination/density,
          // but opening panels on navigation is a bad UX.
          const {
            density,
            filter,
            sorting,
            preferencePanel,
            pagination: savedPagination,
            ...state
          } = displayPreference;
          // Extra safety for older/newer persisted shapes.
          // @ts-ignore
          delete state.preferencePanel;
          // @ts-ignore
          delete state.preferencesPanel;
          // paginationModel is controlled. Restoring it through the grid api
          // fights the prop and loops inside the grid reducer.
          // @ts-ignore
          delete state.pagination;

          state.columns = renameSavedColumns(state.columns || {}, renamedFieldsRef.current);
          state.columns.columnVisibilityModel = savedColumnVisibility(
            initialColumnVisibilityDefaultsRef.current,
            state.columns.columnVisibilityModel,
            state.columns.orderedFields
          );

          //get density and set to datagrid
          if (density) {
            apiRef.current.setDensity(density.value as GridDensity);
          }

          // Apply saved pagination through React state only.
          const savedModel = savedPagination?.paginationModel;
          if (savedModel) {
            const savedPage = Number(savedModel.page);
            const savedPageSize = Number(savedModel.pageSize);
            setPagination(name, {
              page: Number.isFinite(savedPage) ? savedPage : 0,
              pageSize: Number.isFinite(savedPageSize) ? savedPageSize : initialPageSize,
            });
            setPage(Number.isFinite(savedPage) ? savedPage : 0);
            setPageSize(Number.isFinite(savedPageSize) ? savedPageSize : initialPageSize);
          }
          console.debug("State: ");
          console.dir(state);
          apiRef.current.restoreState(state);
        }
      });
    }
  }, [name, apiRef, setPagination, initialPageSize]);

  const defaultFilters = useMemo(() => {
    let filter: GridFilterModel = {
      items: [],
    };

    if (defaultItems && defaultItems.length > 0) {
      // deep copy defaultItems so filters can be restored when using back button
      filter.items = defaultItems.map((obj) => ({ ...obj }));
    }
    return filter;
  }, [defaultItems]);

  /**
   * Sets the text in the quick search from the 'q' query parameter
   */
  

  /**
   * Sets the filter model from:
   * - advancedSearchProps.items
   * - 'filter' query parameters
   */


  /**
   * Sets the page from the page query parameter or resets it to the first page
   */
  useEffect(() => {
    console.debug("Page effect");
    const storedPage =
      pagination.hasOwnProperty(name) && pagination[name]
        ? Number(pagination[name].page)
        : 0;
    const nextPage = Number.isFinite(storedPage) ? storedPage : 0;
    setPage((current) => (current === nextPage ? current : nextPage));
  }, [pagination, name]);

  // /**
  //  * Sets the page size from the pageSize query parameter
  //  */
  useEffect(() => {
    console.debug("Page size effect");
    // if (pageSizeQueryParam) {
    //   setPageSize(pageSizeQueryParam);
    // } else {
    //   setPageSize(15);
    // }
    // No SAVED size for this grid -> fall back to the caller's initial, not a
    // literal 15. This effect runs on mount, so a hardcoded literal here is
    // what silently undid `useState(initialPageSize)` above: the grid seeded
    // at 100 and was reset to 15 before first paint. The saved-size branch
    // still wins, which is the intended precedence.
    // Numeric + no-op when unchanged (upstream d400eabe4): a non-numeric saved
    // size, or a setState that changes nothing, fed the pagination loop that
    // froze the stock list.
    const storedPageSize =
      pagination.hasOwnProperty(name) && pagination[name]
        ? Number(pagination[name].pageSize)
        : initialPageSize;
    const nextPageSize = Number.isFinite(storedPageSize) ? storedPageSize : initialPageSize;
    setPageSize((current) =>
      current === nextPageSize ? current : nextPageSize
    );
  }, [pagination, name, initialPageSize]);

  /** Stable identity when page/pageSize unchanged — avoids MUI pagination sync + onPaginationModelChange on unrelated re-renders (e.g. slide-in opening). */
  const paginationModel = useMemo(
    () => ({ page, pageSize }),
    [page, pageSize]
  );

  /**
   * Builds the filters and calls the data callback setting the rows and rowcount from the result
   */
  useEffect(() => {
    const controller = new AbortController();

    async function getData() {
      console.debug("Get data effect: ");
      console.debug(data);
      console.debug(page);
      console.debug(pageSize);
      console.debug("filter:");
      console.debug(filter);
      console.debug("sort:");
      console.debug(sortModel);
      setLoading(true);

      const params = { page: page + 1, size: pageSize };
      const filters: string[] = [];

      if (filter) {
        for (const f of filter.items) {
          if (f.field) {
            // special case for quick search
            if (f.field === "q") {
              params["q"] = f.value;
              continue;
            }

            if ((f.value != null && f.value !== "") || f.operator === "!=") {
              let op = f.operator || "contains";
              let value = f.value;
              if (op === "contains") {
                op = "ilike";
                value = "%" + f.value + "%";
              } else if (op === "startsWith") {
                op = "ilike";
                value = f.value + "%";
              } else if (op === "endsWith") {
                op = "ilike";
                value = "%" + f.value;
              }
              if (f.field.includes(".")) {
                const parts = f.field.split(".", -2);
                let [model, field] = parts.slice(-2);
                model = model[0].toUpperCase() + model.slice(1);
                filters.push(
                  JSON.stringify({
                    model: model,
                    field: field,
                    op: op,
                    value: value,
                  })
                );
              } else {
                filters.push(
                  JSON.stringify({
                    field: f.field,
                    op: op,
                    value: value,
                  })
                );
              }
            }
          }
        }
      }
      if (filters.length > 0) {
        params["filter"] = filters;
      }

      const sortFields: string[] = [];
      if (sortModel) {
        for (const sortItem of sortModel) {
          let field = sortItem.field;
          if (sortItem.sort && sortItem.sort === "desc") {
            field = "-" + field;
          }
          sortFields.push(field);
        }
      }
      if (sortFields.length > 0) {
        params["sort"] = sortFields.toString();
      }

      try {
        const results: QueryResults<T> = await data(
          params,
          {
            signal: controller.signal,
            cache: false,
          },
          apiRef
        );
        // P2d D3: a SUPERSEDED fetch (its filter, page, sort or the caller's
        // data moved on, so the cleanup aborted it) never writes. Its result
        // can settle long after the fetch that replaced it — a cancelled
        // read surfaced ~42 s late through api.ts's cold-start ladder — and a
        // caller that answers a cancel with an empty page (the Flight & Duty
        // list) then blanked the live rows: "No results were found within the
        // applied filter", with nothing on the wire.
        if (controller.signal.aborted) return;
        setRowCount(results.total);
        setRows(results.rows);
        setLoading(false);
      } catch (e) {
        // P2d D3: nor does a superseded fetch's failure — the live fetch owns
        // the rows and the spinner.
        if (controller.signal.aborted) return;
        // Clear the rows too: without this a failed refetch leaves the PREVIOUS
        // page's rows on screen under an error toast, i.e. stale data presented
        // as the result of the request that just failed.
        setRows([]);
        setRowCount(0);
        setLoading(false);
      }
    }

    if (currentUserId) {
      getData();
    }

    return () => {
      controller.abort();
    };
  }, [apiRef, data, currentUserId, page, pageSize, filter, sortModel]);

  useEffect(() => {
    const handleClick = (event) => {
      if (event.target.id === "action") {
        setButtonText("action");
      } else {
        setButtonText("");
      }
    };

    window.addEventListener("click", handleClick); // Log all clicks

    return () => {
      window.removeEventListener("click", handleClick);
    };
  }, []);

  const onFilterChange = useCallback(
    (filterModel: GridFilterModel, details: GridCallbackDetails<"filter">) => {
      console.debug("onFilterChange");
      // we're hijacking the restoreState reason to also mean loading filter from query params
      // in this case we only want to set the filter we don't want to reset the query params and
      // navigate again
      filterModel.items = filterModel.items
        .filter((obj) => obj.value !== null && obj.value !== "")
        .map((obj) => ({ ...obj }));
      console.log(filterModel,"compare ",filter)
      // Reset to first page when the filter set changes — but not for programmatic
      // restoreState (e.g. closing advanced-search slide), which would otherwise
      // force page 0 and cascade into onPaginationModelChange / store updates.
      if (
        details.reason !== "restoreState" &&
        filter &&
        filter?.items !== filterItems
      ) {
        apiRef.current.setPage(0);
      }

      if (details.reason === "restoreState") {
        console.debug("Filters: ");
        console.dir(filterModel);
        setFilter(filterModel);
        setFilterVisibleModel(filterModel);
        setSearchField("");
        setSearchText("");
      } 
      
      else if (buttonText.length > 0) {
        console.log("here action is triggered");
      } 
      else {
        console.debug("navigating with filter");
        // The filter to keep: the panel's items with a value, without the quick search.
        const saveFilter = (filterModel?.items ?? []).filter(
          (item: GridFilterItem) =>
            (!filterObj || item.field !== filterObj.field) &&
            !["", null, undefined].includes(item.value)
        );
        // Staged for Display Preferences → Save, never written here, and only
        // for the user's own Filters-panel edit (not a restore, advanced search
        // or a reason-less sync).
        if (
          USER_FILTER_EDIT_REASONS.has(String(details.reason)) &&
          !advanceSearchTriggered
        ) {
          pendingFilterRef.current = saveFilter;
        }

        // the logic toinclude quick search in filter model when both applied
        if(filterObj){
          const existingFilterItem = filterModel.items.find((item) => item.field === filterObj?.field && item.value ===undefined);
          if(existingFilterItem){

            setFilter(filterModel);
          }
          else{
            const existingFilter = filterModel.items.find((item) => item.field === filterObj.field && item.value !==undefined);
            if(existingFilter){
              setFilter(filterModel);
            }
            else{
              
              setFilter({items:[...filterModel.items,filterObj]});
            }
          }
        }
        else{
          setFilter(filterModel);
        }

        //logic to display only filter panel field not the quick filter in filter panel
        let newFilter = filterModel?.items.filter((item: GridFilterItem) => {
            return item.field !== filterObj?.field;
          });
        if (newFilter) {
          
          setFilterVisibleModel({items:[...newFilter]});
        }
        else{
          setFilterVisibleModel(filterModel);
        }
      }

      // Reset the search state when the user clears all filters, or on a
      // reason-less change (the quick-search clear; MUI's own syncs, e.g.
      // showFilterPanel). Neither writes: a clear-all is staged above like any
      // panel edit, and a reason-less change never touches the saved filter.
      if (String(details.reason) === "removeAllFilterItems" || !details.reason) {
        setFilterItems([]);
        setFilter(filterModel);
        setFilterVisibleModel(filterModel);
        setSearchField("q");
        setSearchText("");
        setQuickSearchField("q");
        setQuickSearchValue("");
        setFilterActive(false);
      }
    },
    [
      buttonText,
      filterObj,
      setFilter,
      filterItems,
      advanceSearchTriggered,
      setQuickSearchField,
      setQuickSearchValue,
      setFilterActive,
      setFilterVisibleModel,
      apiRef,
      filter
    ]
  );

  const { onRowSelectionModelChange } = props;
  const onSelectionChange = useCallback(
    (newSelectionModel: GridRowSelectionModel) => {
      setSelectionModel(newSelectionModel);
      if (typeof onRowSelectionModelChange === "function") {
        onRowSelectionModelChange(newSelectionModel);
      }
    },
    [onRowSelectionModelChange]
  );

  const onSortChange = useCallback(
    (newSortModel: GridSortModel, details: GridCallbackDetails<any>) => {
      console.debug("onSortChange", newSortModel);
      console.debug(details);

      if (newSortModel && newSortModel.length > 0) {
        setSortModel(newSortModel);
      } else {
        if (initialSort) {
          setSortModel(initialSort);
        } else {
          setSortModel([]);
        }
      }
    },
    [initialSort]
  );

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel, details: GridCallbackDetails<any>) => {
      const nextPage = Number(model.page);
      const nextPageSize = Number(model.pageSize);
      if (
        (Number.isFinite(nextPage) ? nextPage : 0) === page &&
        (Number.isFinite(nextPageSize) ? nextPageSize : pageSize) === pageSize
      ) {
        return;
      }
      console.debug("handlePaginationModelChange");
      const pageToStore = Number.isFinite(nextPage) ? nextPage : 0;
      const pageSizeToStore = Number.isFinite(nextPageSize) ? nextPageSize : initialPageSize;
      // In-session only. The page size is saved by Display Preferences → Save
      // (exportState carries the pagination model), not on every page turn.
      setPagination(name, { page: pageToStore, pageSize: pageSizeToStore });
      setPage(pageToStore);
      setPageSize(pageSizeToStore);
    },
    [page, pageSize, setPageSize, setPage, setPagination, name, initialPageSize]
  );
  const onClose = useCallback(() => {
    setAction(Actions.None);
    setSelectionModel([]);
    if (setSelected) {
      setSelected(undefined);
    }
  }, [setSelected]);

  /**
   * Clear the quick search field and the query parameters
   */
  const onClearSearch = useCallback(() => {
    setSearchText("");

    // to remove quick search on cancel button from filter list
    if (filterObj) {
      setSearchField("");
      setQuickSearchField("q")
      setQuickSearchValue("")
      setFilterObj(undefined);
      resetQuickFilter();
      
      //to apply the other filter by removing quick search from filter model
      let newFilter = filter?.items.filter((item: GridFilterItem) => {
        return item.field !== filterObj.field;
      });
      if (newFilter) {
        apiRef.current.setFilterModel({items:newFilter});
      }

      // to reset quick search on clear button
    }
  }, [filterObj, filter, apiRef,resetQuickFilter,setQuickSearchField,setQuickSearchValue]);

  const onClearAdvancedSearch = useCallback(() => {
    if (filterObj) {
      let newFilter = filter?.items.filter((item: GridFilterItem) => {
        return item.field !== filterObj.field;
      });
      if (newFilter) {
        apiRef.current.upsertFilterItems(newFilter);
      }

      setSearchField("");
      setQuickSearchField("q")
      setFilterObj(undefined);
    } else {
      apiRef.current.setFilterModel(defaultFilters, "restoreState");
    }
  }, [
    apiRef,
    defaultFilters,
    filterObj,
    filter,
    setQuickSearchField,
  ]);

  const onCloseAdvancedSearch = useCallback(() => {
    setAction(Actions.None);
  }, []);
  
  const onCloseAdvancedSearchSlide = useCallback(() => {
    setAction(Actions.None);
    setAdvanceSearchClicked(false);
    setAdvanceSearchTriggered(false);
    setAdvanceSearch(name, []);
    apiRef.current.setFilterModel(defaultFilters, "restoreState");
  }, [setAdvanceSearchClicked,setAdvanceSearchTriggered,setAdvanceSearch,name,defaultFilters,apiRef]);

  const onCloseAdvancedSearchIcon = useCallback(() => {
    setAction(Actions.None);
    setAdvanceSearchClicked(false);
    setAdvanceSearchTriggered(false);
    setAdvanceSearch(name, []);
    apiRef.current.setFilterModel(defaultFilters, "restoreState");
  }, [setAdvanceSearchClicked,setAdvanceSearchTriggered,setAdvanceSearch,name,defaultFilters,apiRef]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(searchText);
    }, 200); // 500ms debounce

    return () => clearTimeout(handler);
  }, [searchText]);

 useEffect(() => {
  if (filterObj) {
    const filterModel = gridFilterModelSelector(apiRef);
    const existingFilterItem = filterModel.items.find((item) => item.field === filterObj.field && item.value !== undefined);
    if (existingFilterItem) {
      apiRef.current.deleteFilterItem(existingFilterItem);
    }
    setQuickFilter(name, filterObj);
    const isEmpty =
      filterObj.value === undefined ||
      filterObj.value === "" ||
      (Array.isArray(filterObj.value) && filterObj.value.length === 0);
    if (!isEmpty) {
      const valueToApply =
        filterObj.value !== undefined && filterObj.value !== null
          ? filterObj.value
          : debouncedValue;
      const fieldToApply =
        filterObj.value !== undefined && filterObj.value !== null
          ? filterObj.field
          : searchField;
      apiRef.current.upsertFilterItem({
        field: fieldToApply,
        operator: filterObj.operator,
        value: valueToApply,
      });
    }
  }
}, [debouncedValue, filterObj, apiRef, searchField, name, setQuickFilter]);

  return (
    <Box display="flex" height="100%" maxWidth="100%">
      <Box flexGrow={1} maxWidth="100%">
        {/* @ts-ignore */}
        <DataGridPro
          apiRef={apiRef}
          autoHeight
          columnHeaderHeight={52}
          checkboxSelection={checkboxSelection}
          slots={{
            ...components,
            toolbar: components.toolbar,
            noRowsOverlay: () => (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                <Box sx={{ textAlign: "center" }}>
                  {(filterObj || filterActive)
                    ? "No results were found within the applied filter"
                    : advanceSearchTriggered
                      ? "No results were found"
                      : (noRowsMessage ?? "No rows")}
                </Box>
              </Box>
            ),
            // Consumer slots merge ON TOP rather than replacing the object (see
            // the destructure note above). Additive by default; still lets a
            // consumer override one slot deliberately.
            ...(consumerSlots ?? {}),
          }}
          slotProps={{
            baseSelect: {
              native: false,
            },
            ...(consumerSlotProps ?? {}),
            toolbar: {
              toolbarExtras,
              settingsMenuExtras,
              name:name,
              action: action,
              actions: actions,
              clearSearch: onClearSearch,
              defaultItems: defaultItems,
              enableAdvancedSearch: enableAdvancedSearch,
              enableCreate: enableCreate,
              createMenu: props.createMenu,
              onCreate: props.onCreate,
              enableDelete: enableDelete,
              enableFilter: enableFilter,
              exportState: exportState,
              // @ts-ignore
              onChange: (event, searchFieldText, filterObj) => {
                setSearchField(searchFieldText);
                setSearchText(event.target.value);
                setFilterObj(filterObj);
              },
              setAction: setAction,
              setSelected: setSelected,
              setSelectionModel: onSelectionChange,
              value:searchText,
              selectionModel: selectionModel,
              onDelete: onDelete,
              printOptions: {
                hideFooter: true,
                hideToolbar: true,
                fileName: exportFileName,
              },
              csvOptions: {
                fileName: exportFileName,
              },
              quickSearchItems: quickSearchItems,
              setFilterButtonEl,
              setAdvanceSearchClicked: setAdvanceSearchClicked,
              advanceSearchClicked: advanceSearchClicked,
              getLocalStorageKey: getLocalStorageKey,
              currentUserId: currentUserId,
              setFilterObj:setFilterObj,
              ...(consumerSlotProps?.toolbar ?? {}),
            },
            panel: {
              anchorEl: filterButtonEl,
              ...(consumerSlotProps?.panel ?? {}),
            },
            filterPanel: {
              logicOperators: [GridLogicOperator.And],
              columnsSort: "asc",
              filterFormProps: {
                logicOperatorInputProps: {
                  variant: "outlined",
                  size: "small",
                  margin: "dense",
                },
                columnInputProps: {
                  variant: "outlined",
                  size: "small",
                  margin: "dense",
                },
                operatorInputProps: {
                  variant: "outlined",
                  size: "small",
                  margin: "dense",
                },
                valueInputProps: {
                InputComponentProps: {
                  variant: "outlined",
                  size: "small",
                  margin: "dense",
                },
                variant: "outlined",
                size: "small",
                margin: "dense",
              },
              },
             sx: {
  "& .MuiDataGrid-columnHeader": { color: grey[900] },
  "& .MuiDataGrid-columnHeaders": {
    borderTop: `${DATA_GRID_HEADER_KEYLINE} !important`,
    borderTopColor: "#00000063",
  },
  "& .MuiDataGrid-filterForm": {
  padding: "30px 40px 10px 25px", // top right bottom left
  backgroundColor: "#eeeeee",
  alignItems: "center",
},
  "& .MuiDataGrid-filterForm button.MuiButtonBase-root.MuiButton-root": {
    marginLeft: "45px",
  },
  "& .MuiDataGrid-filterFormLogicOperatorInput": {
    mr: 2,
  },
  "& .MuiDataGrid-filterFormColumnInput": {
    mr: 2,
    width: 150,
  },
  "& .MuiDataGrid-filterFormOperatorInput": {
    mr: 2,
  },
  "& .MuiDataGrid-filterFormValueInput": {
    width: 200,
  },
  "& .MuiDataGrid-filterFormValueInput .MuiFormControl-root": {
    marginTop: "0px",
    marginBottom: 0,
    minWidth: "200px",
  },
  // Add these new styles to fix the label/outline issue:
  "& .MuiDataGrid-filterFormValueInput .MuiInputLabel-root": {
    backgroundColor: "#eeeeee", // Match the filter form background
    paddingLeft: "2px",
    paddingRight: "8px",
  },
  "& .MuiDataGrid-filterFormValueInput .MuiOutlinedInput-notchedOutline legend": {
    maxWidth: "100%", // Ensures the legend (notch) displays properly
  },
  "& .MuiFormControl-root": {
    "&.MuiFormControl-fullWidth": {
      width: "auto",
    },
  },
  "& .MuiDataGrid-panelFooter": {
    backgroundColor: "#eeeeee",
  },
  "& .MuiButtonBase-root.MuiButton-root": {
    marginLeft: "25px",
    marginRight: "30px",
    marginBottom: "15px",
  },
},
              ...(consumerSlotProps?.filterPanel ?? {}),
            },
          }}
          filterModel={filterVisibleModel}
          disableColumnFilter={disableColumnFilter}
          disableColumnSelector={true}
          disableRowSelectionOnClick
          filterMode="server"
          loading={loading}
          onFilterModelChange={onFilterChange}
          onRowSelectionModelChange={onSelectionChange}
          onSortModelChange={onSortChange}
          pagination
          paginationModel={paginationModel}
          paginationMode="server"
          onPaginationModelChange={handlePaginationModelChange}
          rowCount={rowCount}
          rows={rows}
          pageSizeOptions={[15, 50, 100]}
          rowSelectionModel={selectionModel}
          sortModel={sortModel}
          sx={[
            {
              border:"none",
              "& .MuiDataGrid-columnHeaderTitle":{
                fontFamily: '"Roboto-Bold", "Roboto", sans-serif',
                fontWeight: 400,
              },
              "& .MuiDataGrid-columnHeaders": {
                borderTop: `${DATA_GRID_HEADER_KEYLINE} !important`,
                borderBottom: `${DATA_GRID_HEADER_KEYLINE} !important`,
                borderRadius: "0px !important",
              },
              "& .MuiDataGrid-columnHeaderTitleContainer": {
                paddingBottom: 0,
              },
              "& .MuiDataGrid-columnHeader": {
                color: grey[900],
                boxSizing: "border-box",
              },
              ".MuiDataGrid-cell:focus": {
                outline: "none",
              },
              "& .MuiDataGrid-main": {
                // THE listing gutter. This is where the content box's left and
                // right edges come from on every grid module — the app shell
                // (components/AppBackground.tsx) adds no horizontal padding —
                // so a non-grid listing surface has to sit on the same constant
                // rather than pick its own. See utils/dataGridSx.ts.
                margin: `0px ${LISTING_GUTTER}`,
                borderBottom: DATA_GRID_HEADER_KEYLINE,
                borderTop: DATA_GRID_HEADER_KEYLINE,
              },
              "& .MuiDataGrid-withBorderColor": !isHeaderBorder
                ? {
                    borderTop: "0px !important",
                  }
                : {},
              "& .MuiDataGrid-footerContainer": {
                margin: `0px ${LISTING_GUTTER}`,
                borderBottom: `${DATA_GRID_HEADER_KEYLINE} !important`,
                marginBottom: "10px",
              },
              "& .MuiDataGrid-toolbarContainer": {
                alignItems: "center",
              },
            },
            listCellLinkCssSx,
            ...(Array.isArray(sx) ? sx : [sx]),
          ]}
          {...dataGridProps}
        />
        <Suspense fallback={<LoadingSpinner />}>
          {enableAdvancedSearch &&
          advancedSearchForm &&
          filter &&
          defaultItems &&
          action === Actions.AdvancedSearch ? (
            <AdvancedSearch2
              open={true}
              defaultItems={defaultItems}
              onClear={onClearAdvancedSearch}
              onClose={onCloseAdvancedSearch}
              apiRef={apiRef}
              name={name}
              form={advancedSearchForm}
              onCloseAdvancedSearchIcon ={onCloseAdvancedSearchIcon}
              onCloseAdvancedSearchSlide={onCloseAdvancedSearchSlide}
              {...advancedSearchProps}
            />
          ) : (
            <></>
          )}
          {children ? children({ onClose, action, Actions }) : <></>}
        </Suspense>
      </Box>
    </Box>
  );
}

export default AeronetDataGrid;
